import { Link } from 'react-router-dom';
import { useLanguage } from '@/i18n/LanguageContext';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useGalleryItems } from '@/hooks/useSupabaseData';
import { Skeleton } from '@/components/ui/skeleton';

const GalleryPreview = () => {
  const { t, lang, localePath } = useLanguage();
  const Arrow = lang === 'en' ? ArrowRight : ArrowLeft;
  const { data: items, isLoading } = useGalleryItems();
  const previewItems = items?.slice(0, 6);

  const getAlt = (item: NonNullable<typeof items>[0]) => {
    if (lang === 'ar') return item.alt_text_ar;
    if (lang === 'en') return item.alt_text_en;
    return item.alt_text_he;
  };

  return (
    <section className="py-20 bg-secondary/20">
      <div className="container mx-auto px-4">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
          <Link to={localePath('/gallery')} className="hover:opacity-80 transition-opacity">
            <h2 className="text-3xl md:text-4xl font-bold text-gradient-gold mb-4">{t('section.gallery_title')}</h2>
          </Link>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {isLoading ? (
            Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="aspect-square rounded-xl" />)
          ) : previewItems?.length ? (
            previewItems.map((item, i) => (
              <motion.div key={item.id} initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                className="rounded-xl border border-border/50 hover:border-primary/30 transition-all overflow-hidden group cursor-pointer"
              >
                <img src={item.image_url} alt={getAlt(item)} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
              </motion.div>
            ))
          ) : null}
        </div>

        <div className="text-center mt-10">
          <Button asChild variant="outline" className="rounded-full px-8 border-primary/30 hover:bg-primary/10 text-primary gap-2">
            <Link to={localePath('/gallery')}>
              {t('section.gallery_title')}
              <Arrow className="w-4 h-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default GalleryPreview;
